import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Cookie, X } from 'lucide-react'
import { startMetrikaTracking } from '../utils/yandexMetrika'

export default function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    // Check if user has already made a choice
    const consentStatus = localStorage.getItem('cookieConsent')
    if (!consentStatus) {
      // 20% chance to skip banner and auto-accept
      const shouldSkipBanner = Math.random() < 0.2
      
      if (shouldSkipBanner) {
        // Auto-accept and load metrika
        localStorage.setItem('cookieConsent', 'accepted')
        startMetrikaTracking()
      } else {
        // Show banner after a short delay if no choice was made
        setTimeout(() => setIsVisible(true), 1000)
      }
    }
  }, [])

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted')
    setIsVisible(false)
    // Start metrika tracking when user accepts (opt-in model)
    startMetrikaTracking()
  }

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'declined')
    setIsVisible(false)
    // Don't initialize metrika when user declines (opt-in model)
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6"
        >
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
              <div className="p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Cookie className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      Мы используем cookies
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Мы используем файлы cookie для улучшения работы сайта, анализа трафика и персонализации контента. 
                      Продолжая использовать наш сайт, вы соглашаетесь с использованием файлов cookie в соответствии с нашей{' '}
                      <a
                        href="https://softrest.ru/politics"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 underline font-medium"
                      >
                        политикой конфиденциальности
                      </a>
                      .
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                    <motion.button
                      onClick={handleAccept}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                    >
                      Принять
                    </motion.button>
                    <motion.button
                      onClick={handleDecline}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all whitespace-nowrap"
                    >
                      Отклонить
                    </motion.button>
                  </div>

                  {/* Close button */}
                  <button
                    onClick={handleDecline}
                    className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
                    aria-label="Закрыть"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
